'use client';

import React from 'react';

interface ParsedStep {
    n: number;
    bullets: string[];
}

interface StructuredStepTextProps {
    text: string;
    renderStepExtras?: (step: ParsedStep) => React.ReactNode;
}

export default function StructuredStepText({ text, renderStepExtras }: StructuredStepTextProps) {
    const lines = (text || '').split('\n');
    let intro = '';
    const steps: ParsedStep[] = [];
    const summary: string[] = [];
    let current: ParsedStep | null = null;

    for (const raw of lines) {
        const line = raw.trim();
        if (!line) continue;
        if (!intro && line.startsWith('We are solving:')) {
            intro = line;
            continue;
        }
        const m = line.match(/^Step\s+(\d+):/i);
        if (m) {
            if (current) steps.push(current);
            current = { n: Number(m[1]), bullets: [] };
            continue;
        }
        if (line.startsWith('- ')) {
            if (!current) {
                current = { n: steps.length + 1, bullets: [] };
            }
            current.bullets.push(line.slice(2));
            continue;
        }
        summary.push(line);
    }
    if (current) steps.push(current);

    return (
        <div className="flex flex-col gap-4" role="status" aria-live="polite">
            {intro && <p className="text-sm text-muted">{intro}</p>}
            {steps.map((s) => (
                <div
                    key={s.n}
                    className="rounded-md border-[0.5px] border-line bg-surface p-4"
                >
                    <div className="mb-2">
                        <span className="inline-flex rounded-sm bg-subtle px-2 py-0.5 text-[11px] font-medium text-muted">
                            Step {s.n}
                        </span>
                    </div>
                    {s.bullets && s.bullets.length > 0 && (
                        <ul className="flex list-none flex-col gap-1">
                            {s.bullets.map((b, i) => (
                                <li key={i} className="before:mr-2 before:text-hint before:content-['•']">
                                    {b}
                                </li>
                            ))}
                        </ul>
                    )}
                    {typeof renderStepExtras === 'function' && (
                        <div>{renderStepExtras(s)}</div>
                    )}
                </div>
            ))}
            {summary.length > 0 && (
                <div className="mt-4 border-t-[0.5px] border-line pt-4">
                    {summary.map((l, i) => {
                        const low = l.toLowerCase();
                        if (low.startsWith('answer:')) {
                            return (
                                <p key={i} className="text-base font-medium text-teal">
                                    {l}
                                </p>
                            );
                        }
                        if (low.startsWith('remainder')) {
                            return (
                                <p key={i} className="text-[13px] text-muted">
                                    {l}
                                </p>
                            );
                        }
                        return <p key={i}>{l}</p>;
                    })}
                </div>
            )}
        </div>
    );
}
